import { SelectionModel } from '@angular/cdk/collections';
import { FlatTreeControl } from '@angular/cdk/tree';
import {
  Component,
  ElementRef,
  Injectable,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  MatTreeFlatDataSource,
  MatTreeFlattener,
} from '@angular/material/tree';
import { BehaviorSubject } from 'rxjs';

import * as _ from 'lodash';
import { GeneralService } from 'src/app/services/general.service';
import { MatDialog } from '@angular/material/dialog';
import { FileDetailsDialogComponent } from './file-details-dialog/file-details-dialog.component';

//#region Tree

/** Flat to-do item node with expandable and level information */
export class TreeFlatNode {
  item: string;
  files: any[] = [];
  level: number;
  expandable: boolean;
}

class TreeNode {
  name: string;
  files: any[] = [];
  child: TreeNode[] = [];

  constructor(name: string) {
    this.name = name;
  }

  addChild(node) {
    this.child.push(node);
  }
}
/**
 * Checklist database, it can build a tree structured Json object.
 * Each node in Json object represents a to-do item or a category.
 * If a node is a category, it has children items and new items can be added under the category.
 */
@Injectable()
export class ChecklistDatabase {
  dataChange = new BehaviorSubject<TreeNode[]>([]);

  get data(): TreeNode[] {
    return this.dataChange.value;
  }

  constructor() {
    this.initialize();
  }

  rawData = [
    { fileName: 'File1.xlsx', path: '/' },
    { fileName: 'File29.xlsx', path: '/' },
    { fileName: 'File30.xlsx', path: '/' },
    { fileName: 'File31.xlsx', path: '/' },
    { fileName: 'File32.xlsx', path: '/' },
    { fileName: 'File33.xlsx', path: '/1' },
    { fileName: 'File34.xlsx', path: '/1' },
    { fileName: 'File35.xlsx', path: '/1' },
    { fileName: 'File36.xlsx', path: '/1' },
    { fileName: 'File3894.xlsx', path: '/2' },
    { fileName: 'File3784.xlsx', path: '/2' },
    { fileName: 'File39.xlsx', path: '/1/2' },
    { fileName: 'File40.xlsx', path: '/1/2/3' },
    { fileName: 'File41.xlsx', path: '/1/2/3' },
    { fileName: 'File42.xlsx', path: '/1/2/3' },
    { fileName: 'File43.xlsx', path: '/1/2/3' },
    { fileName: 'File44.xlsx', path: '/1/2/3/4' },
    { fileName: 'File45.xlsx', path: '/1/2/3/4' },
    { fileName: 'File46.xlsx', path: '/1/2/3/4' },
    { fileName: 'File47.xlsx', path: '/1/2/3/4' },
    { fileName: 'File48.xlsx', path: '/1/2/3/4' },
    { fileName: 'File50.xlsx', path: '/2/3' },
    { fileName: 'File49.xlsx', path: '/2/3/4' },
    { fileName: 'File51.xlsx', path: '/2/3/4' },
    { fileName: 'File52.xlsx', path: '/2/3/4' },
    { fileName: 'File53.xlsx', path: '/2/3/4' },
    { fileName: 'File54.xlsx', path: '/2/3/4' },
    { fileName: 'File55.xlsx', path: '/2/4' },
  ];

  flatNodes = [];

  initialize() {
    let data: TreeNode;
    data = this.BuildTreeView();
    this.dataChange.next([data]);
  }

  BuildTreeView(): TreeNode {
    let distinct = _.uniq(this.rawData.map((x) => x.path));
    let allNodes: TreeNode[] = [];
    let _distinct = _.values(distinct);
    _distinct.forEach((element) => {
      allNodes.push(new TreeNode(element));
    });

    let firstNodes = _.values(
      _.uniq(allNodes.map((x) => '/' + x.name.split('/')[1]))
    );
    firstNodes.forEach((element) => {
      let f = allNodes.find((x) => x.name == element);
      if (!f) {
        allNodes.push(new TreeNode(element));
      }
    });

    for (let index = 0; index <= 10; index++) {
      let elementsOfLevel = allNodes.filter(
        (x) => x.name.split('/').length == index
      );
      if (index == 2) {
        let root = allNodes.find((x) => x.name == '/');
        if (root) {
          root.child = root.child.concat(
            elementsOfLevel.filter((x) => x.name != '/')
          );
        } else {
          root = new TreeNode('/');
          root.child = root.child.concat(
            elementsOfLevel.filter((x) => x.name != '/')
          );
          allNodes.push(root);
        }
      } else {
        elementsOfLevel.forEach((levelElement) => {
          let lastIndexOfSlash = levelElement.name.lastIndexOf('/');
          let parentName = levelElement.name.slice(0, lastIndexOfSlash);
          if (parentName && parentName != '') {
            let parent = allNodes.find((x) => x.name == parentName);
            if (parent) {
              parent.child.push(levelElement);
            }
          }
        });
      }
    }
    allNodes.forEach((element) => {
      let node = this.rawData.filter((x) => x.path == element.name);
      if (node) {
        element.files = node.map((x) => x.fileName);
      }
    });
    var root = allNodes.find((x) => x.name == '/');
    return root;
  }

  // /** Add an item to to-do list */
  insertItem(parent: TreeNode, name: string) {
    if (parent.child) {
      parent.files.push(name);
      this.dataChange.next(this.data);
    }
  }

  insertLevel(parent: TreeNode, name: string) {
    if (parent.child) {
      let node = new TreeNode('NEW-LEVEL');
      // node.child = [new TreeNode("NEW-ITEM")];
      parent.child = parent.child.concat([node]);
      this.dataChange.next(this.data);
    }
  }

  updateItem(node: TreeNode, name: string) {
    node.name = name;
    this.dataChange.next(this.data);
  }
}
//#endregion

@Component({
  selector: 'app-documents',
  templateUrl: './documents.component.html',
  styleUrls: ['./documents.component.scss'],
  providers: [ChecklistDatabase],
})
export class DocumentsComponent implements OnInit {
  constructor(
    private _database: ChecklistDatabase,
    public dialog: MatDialog,
    private generalService: GeneralService
  ) {}

  //#region Tree
  /** Map from flat node to nested node. This helps us finding the nested node to be modified */
  flatNodeMap = new Map<TreeFlatNode, TreeNode>();

  /** Map from nested node to flattened node. This helps us to keep the same object for selection */
  nestedNodeMap = new Map<TreeNode, TreeFlatNode>();

  treeControl: FlatTreeControl<TreeFlatNode>;

  treeFlattener: MatTreeFlattener<TreeNode, TreeFlatNode>;

  dataSource: MatTreeFlatDataSource<TreeNode, TreeFlatNode>;

  flatNodes: any[] = [];

  getLevel = (node: TreeFlatNode) => node.level;

  isExpandable = (node: TreeFlatNode) => node.expandable;

  getChildren = (node: TreeNode): TreeNode[] => node.child;

  hasChild = (_: number, _nodeData: TreeFlatNode) => _nodeData.expandable;

  hasNoContent = (_: number, _nodeData: TreeFlatNode) => _nodeData.item === '';

  /**
   * Transformer to convert nested node to flat node. Record the nodes in maps for later use.
   */
  transformer = (node: TreeNode, level: number) => {
    const existingNode = this.nestedNodeMap.get(node);
    const flatNode =
      existingNode && existingNode.item === node.name
        ? existingNode
        : new TreeFlatNode();
    flatNode.item = node.name;
    flatNode.level = level;
    flatNode.files = node.files;
    flatNode.expandable = !!node.child?.length;
    this.flatNodeMap.set(flatNode, node);
    this.nestedNodeMap.set(node, flatNode);
    return flatNode;
  };

  /* Get the parent node of a node */
  getParentNode(node: TreeFlatNode): TreeFlatNode | null {
    const currentLevel = this.getLevel(node);

    if (currentLevel < 1) {
      return null;
    }

    const startIndex = this.treeControl.dataNodes.indexOf(node) - 1;

    for (let i = startIndex; i >= 0; i--) {
      const currentNode = this.treeControl.dataNodes[i];

      if (this.getLevel(currentNode) < currentLevel) {
        return currentNode;
      }
    }
    return null;
  }
  //#endregion

  FilesNote_DS: any = {};
  emptyFilesNote_DS: any = {};

  sections = [];

  @ViewChild('file') file: ElementRef<HTMLInputElement>;

  //#region Init
  ngOnInit() {
    var num = 12;
    num %= 2;

    //#region Tree
    this.treeFlattener = new MatTreeFlattener(
      this.transformer,
      this.getLevel,
      this.isExpandable,
      this.getChildren
    );
    this.treeControl = new FlatTreeControl<TreeFlatNode>(
      this.getLevel,
      this.isExpandable
    );
    this.dataSource = new MatTreeFlatDataSource(
      this.treeControl,
      this.treeFlattener
    );

    this._database.dataChange.subscribe((data) => {
      this.dataSource.data = data;
    });
    //#endregion
    this.getFilesNoteDS();
  }

  getFilesNoteDS() {
    this.generalService.getDS('FilesNote').subscribe((ds: any) => {
      Object.assign(this.emptyFilesNote_DS, ds.FilesNote[0]);
      Object.assign(this.FilesNote_DS, ds);
    });
  }
  //#endregion

  itemValue;

  //#region Tree
  addFolder(node: TreeFlatNode) {
    const parentNode = this.flatNodeMap.get(node);
    this._database.insertLevel(parentNode!, '');
    this.treeControl.expand(node);
  }

  selectedNode: TreeFlatNode;

  addFile(node: TreeFlatNode) {
    this.selectedNode = node;
    this.file.nativeElement.click();
  }
  saveNode(node: TreeFlatNode, itemValue: string) {
    const nestedNode = this.flatNodeMap.get(node);
    this._database.updateItem(nestedNode!, itemValue);
  }

  onFileSelected(event) {
    var reader2 = new FileReader();
    var src: string;
    reader2.onload = (e) => {
      src = reader2.result.toString();
      let documentName = event.target.files[0].name;
      let documentType = src.substring(0, src.indexOf(',') + 1);
      let documentSrc = src.replace(documentType, '');
      const parentNode = this.flatNodeMap.get(this.selectedNode);
      if (this.selectedNode.files.find((x) => x == documentName) == null) {
        this._database.insertItem(parentNode!, documentName);
      }
      this.treeControl.expand(this.selectedNode);
      this.file.nativeElement.value = '';
    };
    reader2.readAsDataURL(event.target.files[0]);
  }

  openFileDetails(file) {
    this.dialog.open(FileDetailsDialogComponent, {
      data: {
        file: file,
      },
      width: '50vw',
    });
  }

  save() {
    let values = this.flatNodeMap.values();
  }

  //#endregion
}
